// companiesStore.ts
import { create } from "zustand";
import api, { isAbortError } from "../../utils/api";
import { getError } from "../../utils/getError";
import { useVehiclesSocketStore } from "./vehiclesSocketStore";
import { useDevicesStore } from "./devicesStore";
import { useUsersStore } from "./usersStore";

export interface Company {
  id: string;
  name: string;
  business_name: string;
  email: string;
  phone: string | null;
  rfc?: string;
  status_id?: string;
  logo?: string | null;
  website?: string | null;
  centinela_demo?: boolean;
  mocks_report?: boolean;
  contact_person?: {
    name: string;
    phone: string | null;
    email: string | null;
  };
  contact_address?: {
    address1: string;
    address2: string | null;
    city: string;
    state: string;
    country: string;
    zip_code: string;
  };
  billing_address?: {
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    zip_code: string | null;
  } | null;
  devices?: string[];
  settings?: {
    device_alert: boolean;
    interval: number;
    months_backup: number;
    fuel_liters_alert: number | null;
  };
  updatedAt?: { _seconds: number; _nanoseconds: number };
  createdAt?: { _seconds: number; _nanoseconds: number };
  collections?: string[];
}

export interface CreateCompanyData {
  name: string;
  email: string;
  rfc?: string;
  phone: string;
  business_name: string;
  status?: string;
  status_id?: string;
  /** Empresa del CRM (#1070): crear con su nombre o vincular una existente ("" desvincula). */
  crmCreate?: boolean;
  crmCompanyId?: string;
  website?: string;
  origin?: string[];
  account_condition?: string;
  centinela_demo?: boolean;
  mocks_report?: boolean;
  main_user?: {
    user_name?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    username?: string;
    password?: string;
    map_interval?: number | string;
    not_reporting_alert?: boolean;
  };
  carrierId?: string;
  contact_person: {
    name: string;
    phone: string;
    email: string;
  };
  contact_address: {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    country: string;
    zip_code: string;
  };
  billing_address?: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    country?: string;
    zip_code?: string;
  };
  devices?: string[];
}

interface CompaniesStore {
  companies: Company[];
  allCompanies: Company[];
  allCompaniesLoading: boolean;
  loading: boolean;
  error: string | null;
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };
  companyId: string | null;
  companyIdReseller: string | null;
  companyUser: string | null;
  fetchCompanies: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
  }) => Promise<void>;
  fetchAllCompanies: (force?: boolean) => Promise<void>;
  fetchCompany: (id: string) => Promise<Company>;
  createCompany: (
    data: CreateCompanyData,
    logoFile?: File | null,
  ) => Promise<Company>;
  updateCompany: (
    id: string,
    data: Partial<CreateCompanyData>,
    logoFile?: File | null,
  ) => Promise<Company>;
  deleteCompany: (id: string) => Promise<void>;
  setCompanyId: (id: string | null) => void;
  setCompanyUser: (id: string | null) => void;
}

// Secuencia de petición: solo la última búsqueda aplica su respuesta
// (evita que una respuesta lenta de una búsqueda previa pise a la actual).
let companiesReqSeq = 0;

export const useCompaniesStore = create<CompaniesStore>((set, get) => ({
  companies: [],
  allCompanies: [],
  allCompaniesLoading: false,
  loading: false,
  error: null,
  meta: undefined,
  companyId: localStorage.getItem("companyId"),
  companyIdReseller: "3bBceDQdrzCgi5kcV6gS",
  companyUser: localStorage.getItem("userCompanyId") || "",
  fetchCompanies: async (params = {}) => {
    const reqId = ++companiesReqSeq;

    set({ loading: true, error: null });
    try {
      const { data } = await api.get("/companyList", {
        params: {
          limit: params.limit ?? 8,
          page: params.page ?? 1,
          ...params,
        },
      });
      // Solo aplica la respuesta de la última petición (evita pisar con búsquedas viejas)
      if (reqId !== companiesReqSeq) return;
      set({ companies: data.data || [], meta: data.meta, loading: false });
    } catch (err: any) {
      if (reqId !== companiesReqSeq || isAbortError(err)) return;
      set({ error: getError(err), loading: false });
    }
  },

  fetchAllCompanies: async (force = false) => {
    if (get().allCompaniesLoading) return;
    if (!force && get().allCompanies.length > 0) return;

    set({ allCompaniesLoading: true });
    try {
      const { data } = await api.get("/companyListAll");
      const companies = data.data?.data || [];

      set({ allCompanies: companies, allCompaniesLoading: false });
    } catch (err: any) {
      set({ error: getError(err), allCompaniesLoading: false });
    }
  },

  fetchCompany: async (id: string) => {
    const { data } = await api.get("/company", { params: { id } });
    return data.data;
  },

  createCompany: async (companyData, logoFile = null) => {
    const formData = new FormData();

    // Agregar campos principales
    formData.append("name", companyData.name);
    formData.append("email", companyData.email);
    formData.append("phone", companyData.phone);
    formData.append("business_name", companyData.business_name);
    if (companyData.status) formData.append("status", companyData.status);
    if (companyData.status_id)
      formData.append("status_id", companyData.status_id);

    if (companyData.rfc) formData.append("rfc", companyData.rfc);
    if (companyData.website) formData.append("website", companyData.website);
    if (companyData.origin && companyData.origin.length > 0)
      formData.append("origin", JSON.stringify(companyData.origin));
    if (companyData.account_condition)
      formData.append("account_condition", companyData.account_condition);
    if (companyData.centinela_demo !== undefined)
      formData.append("centinela_demo", String(companyData.centinela_demo));
    if (companyData.mocks_report !== undefined)
      formData.append("mocks_report", String(companyData.mocks_report));
    if (companyData.main_user)
      formData.append("main_user", JSON.stringify(companyData.main_user));
    if (companyData.carrierId)
      formData.append("carrierId", companyData.carrierId);
    // Empresa del CRM (#1070): crear con su nombre o vincular una existente.
    if (companyData.crmCreate) formData.append("crmCreate", "true");
    if (companyData.crmCompanyId)
      formData.append("crmCompanyId", companyData.crmCompanyId);

    // Agregar contact_person como JSON
    formData.append(
      "contact_person",
      JSON.stringify(companyData.contact_person),
    );

    // Agregar contact_address como JSON
    formData.append(
      "contact_address",
      JSON.stringify(companyData.contact_address),
    );

    // Agregar billing_address si existe
    if (companyData.billing_address) {
      formData.append(
        "billing_address",
        JSON.stringify(companyData.billing_address),
      );
    }

    // Agregar devices si existen
    if (companyData.devices && companyData.devices.length > 0) {
      formData.append("devices", JSON.stringify(companyData.devices));
    }

    // Agregar logo si existe
    if (logoFile) {
      formData.append("logoFile", logoFile);
    }

    const { data } = await api.post("/company", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return data.data;
  },

  updateCompany: async (id: string, companyData, logoFile = null) => {
    const formData = new FormData();

    // Agregar campos principales
    if (companyData.name) formData.append("name", companyData.name);
    if (companyData.email) formData.append("email", companyData.email);
    if (companyData.phone) formData.append("phone", companyData.phone);
    if (companyData.business_name)
      formData.append("business_name", companyData.business_name);
    if (companyData.status) formData.append("status", companyData.status);
    if (companyData.status_id)
      formData.append("status_id", companyData.status_id);
    if (companyData.rfc) formData.append("rfc", companyData.rfc);
    if (companyData.website) formData.append("website", companyData.website);
    if (companyData.origin !== undefined)
      formData.append("origin", JSON.stringify(companyData.origin || []));
    if (companyData.account_condition !== undefined)
      formData.append("account_condition", companyData.account_condition || "");
    if (companyData.centinela_demo !== undefined)
      formData.append("centinela_demo", String(companyData.centinela_demo));
    if (companyData.mocks_report !== undefined)
      formData.append("mocks_report", String(companyData.mocks_report));
    if (companyData.main_user !== undefined)
      formData.append("main_user", JSON.stringify(companyData.main_user || {}));
    // Empresa del CRM (#1070): "" desvincula; ausente no toca el vínculo.
    if (companyData.crmCreate) formData.append("crmCreate", "true");
    if (companyData.crmCompanyId !== undefined)
      formData.append("crmCompanyId", companyData.crmCompanyId);

    // Agregar contact_person como JSON
    if (companyData.contact_person) {
      formData.append(
        "contact_person",
        JSON.stringify(companyData.contact_person),
      );
    }

    // Agregar contact_address como JSON
    if (companyData.contact_address) {
      formData.append(
        "contact_address",
        JSON.stringify(companyData.contact_address),
      );
    }

    // Agregar billing_address si existe
    if (companyData.billing_address) {
      formData.append(
        "billing_address",
        JSON.stringify(companyData.billing_address),
      );
    }

    // Agregar devices si existen
    if (companyData.devices) {
      formData.append("devices", JSON.stringify(companyData.devices));
    }

    // Agregar logo si existe
    if (logoFile) {
      formData.append("logoFile", logoFile);
    }

    // Log para debugging
    // console.log("Updating company with id:", id);
    // console.log("FormData contents:");
    // for (let [key, value] of formData.entries()) {
    //   console.log(key, value);
    // }

    const { data } = await api.put("/company", formData, {
      params: { id },
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return data.data;
  },

  setCompanyId: (id) => {
    const previousCompanyId = get().companyId;

    if (id) {
      localStorage.setItem("companyId", id);
    } else {
      localStorage.removeItem("companyId");
    }
    set({ companyId: id });

    // Recargar usuarios solo al seleccionar compañía; al limpiar evitamos loading
    // innecesario que provoca parpadeos en layouts (reportes, facturación, etc.).
    if (id) {
      useUsersStore.setState({ users: [], loading: true, meta: undefined });
    } else {
      useUsersStore.setState({ users: [], loading: false, meta: undefined });
    }

    // Si la compañía cambió, resetear devices para forzar recarga con la nueva compañía
    if (previousCompanyId !== id) {
      useDevicesStore.getState().resetAdminSectionDevices();
      // El Sandbox depende de la compañía elegida (owner / plataforma sin compañía
      // propia): se vuelve a consultar con la nueva (sandboxSessionKey).
      void import("../../features/sandbox/sandboxStore")
        .then(({ useSandboxStore }) => useSandboxStore.getState().load())
        .catch(() => undefined);
      // El caso de uso que ordena /apps y el menú también es de la compañía
      // elegida (CC-CONSOLE #1634): misma mecánica.
      void import("../../features/useCase/useCompanyUseCase")
        .then(({ useCompanyUseCaseStore }) => useCompanyUseCaseStore.getState().load())
        .catch(() => undefined);
    }

    // Sincronizar con vehiclesSocketStore
    const currentSelectedCompany =
      useVehiclesSocketStore.getState().selectedCompany;
    if (currentSelectedCompany !== id) {
      useVehiclesSocketStore.setState({
        selectedCompany: id,
        selectedGroup: "all",
      });
    }
  },

  deleteCompany: async (id: string) => {
    await api.delete("/company", {
      params: { id },
    });

    // Cerrar sesión de todos los usuarios de esta compañía
    try {
      await api.post("/auth/logout-company-users", { companyId: id });
    } catch (error) {
      console.warn("No se pudo cerrar sesión de usuarios de la compañía:", error);
    }
  },

  setCompanyUser: (id) => {
    set({ companyUser: id });
  },
}));

export const useDisableCRUD = () => {
  const companyId = useCompaniesStore((state) => state.companyId);
  return !companyId;
};
